import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import styles from "../../assets/styles/Homepage/StudentReviews.module.css";
import dividerImg from "../../assets/images/linedesign.png";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/student-review`;

/* ===================== */
/* Animation Variants    */
/* ===================== */

const headingVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const containerVariant = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const cardVariant = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const StudentReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API);
      setReviews(res.data.data);
      setError(null);
    } catch (error) {
      console.error("Fetch Error:", error);
      setError("Failed to load reviews. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className={styles.reviewSection}>
        <div className="container text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.reviewSection}>
        <div className="container text-center py-5">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </div>
      </section>
    );
  }

  return (
    <motion.section
      className={styles.reviewSection}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <div className="container">
        {/* Heading */}
        <motion.div className="text-center mb-5" variants={headingVariant}>
          <h2 className={styles.heading}>
            Student's Short Review - Student's Experience With Us
          </h2>
          <span className={styles.divider}>
            <img src={dividerImg} alt="divider" />
          </span>
        </motion.div>

        {/* No reviews message */}
        {reviews.length === 0 && (
          <div className="text-center py-4">
            <p className="text-muted">No reviews available yet.</p>
          </div>
        )}

        {/* Cards */}
        <motion.div className="row g-4" variants={containerVariant}>
          {reviews.map((item, index) => (
            <motion.div
              className="col-xl-3 col-lg-3 col-md-6 col-sm-12"
              key={item._id || index}
              variants={cardVariant}
            >
              <motion.div
                className={styles.card}
                whileHover={{
                  scale: 1.04,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                {/* Full Thumbnail Image */}
                <img
                  src={`${ENV_API_BASE_URL}/uploads/${item.img}`}
                  alt={`${item.studentName}'s review`}
                  className={styles.thumbnail}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://via.placeholder.com/300x420?text=No+Image";
                  }}
                />

                {/* Gradient overlay for bottom readability */}
                <div className={styles.overlay} />

                {/* Play Button — centered on image */}
                <motion.a
                  href={item.videoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.playIcon}
                  whileHover={{ scale: 1.15 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className={styles.playBtn} />
                </motion.a>

                {/* Student name + review text — bottom of card */}
                <div className={styles.studentInfo}>
                  <h5 className={styles.studentName}>{item.studentName}</h5>
                  {item.reviewText && (
                    <p className={styles.reviewText}>{item.reviewText}</p>
                  )}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default StudentReviews;